/**
 * "Interested in this treatment?" conversion block.
 *
 * Sits at the bottom of treatment / peptide / article / condition pages.
 * Uses the visit-type-agnostic phrasing per stakeholder guidance
 * (works for in-person clinic visits AND telemedicine where legally
 * appropriate).
 */
import { CheckCircle2 } from "lucide-react";
import { ScheduleConsultationCTA } from "./ScheduleConsultationCTA";

const DEFAULT_POINTS = [
  "Personalized evaluation",
  "Licensed medical provider review",
  "Individualized treatment plan",
  "Ongoing clinical support",
];

export function InterestedInThisTreatment({
  location = "interested-in-treatment",
  topic = "this therapy",
  points = DEFAULT_POINTS,
  variant = "gold",
}) {
  return (
    <section
      data-testid="interested-in-treatment"
      className="rounded-3xl bg-[color:var(--vv-ink)] text-white p-8 md:p-12 my-12 shadow-[0_20px_60px_-20px_rgba(20,184,166,0.35)]"
    >
      <div className="grid md:grid-cols-[1.5fr_1fr] gap-8 items-center">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--vv-teal)] font-semibold mb-2">
            Interested in {topic}?
          </div>
          <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3">
            Meet with one of our licensed medical providers to determine whether
            it's appropriate for you.
          </h3>
          <p className="text-stone-300 leading-relaxed max-w-xl">
            If clinically appropriate, we'll create a personalized treatment
            plan and coordinate your care through appropriate pharmacy channels
            in accordance with applicable laws and regulations.
          </p>
        </div>
        <div className="space-y-3">
          <ul className="space-y-2.5">
            {points.map((p, i) => (
              <li key={`${p.slice(0, 60)}-${i}`} className="flex items-center gap-2 text-stone-200">
                <CheckCircle2 size={18} className="text-[color:var(--vv-teal)] shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <ScheduleConsultationCTA
            location={location}
            variant={variant}
            size="lg"
            fullWidth
          />
        </div>
      </div>
    </section>
  );
}

export default InterestedInThisTreatment;
